import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import React, { useState, type ReactNode } from 'react';
import { Avatar } from '../Avatar';
import { VersionBadge } from '../shared/VersionBadge';
import { PracticeDoctorLabel } from '../shared/PracticeDoctorLabel';
import type { PatientDetail } from '../../services/patients';
import { formatEmail, formatPhone } from '../../utils/contact';
import { formatDateOnly } from '../../utils/dates';

interface PatientInfoPanelProps {
    patient: PatientDetail;
    practiceName: string | null;
    practiceId?: string | null;
    doctorName: string | null;
    manufacturer?: string | null;
    profilePictureUrl?: string;
    profileThumbnailUrl?: string;
    version?: string | null;
    mondayLinks?: {
        patientBoardUrl: string;
        orthoBoardUrl: string | null;
    };
    workQueueUrl?: string | null;
    headerAction?: ReactNode;
}

const FoldableSection: React.FC<{
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
    const [open, setOpen] = useState(defaultOpen);

    return (
        <div className="w-full">
            <button
                type="button"
                className="w-full flex items-center justify-between py-2"
                onClick={() => setOpen(!open)}
            >
                <span className="text-sm font-bold text-gray-800">{title}</span>
                <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 10.17l3.71-2.94a.75.75 0 111.06 1.06l-4.24 3.36a.75.75 0 01-.94 0L5.21 8.29a.75.75 0 01.02-1.08z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>
            {open && <div className="mt-1 mb-1">{children}</div>}
        </div>
    );
};

function safeHref(value: string | null | undefined): string | undefined {
    if (!value) return undefined;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:' ? value : undefined;
    } catch {
        return undefined;
    }
}

const LinkRow: React.FC<{ label: string; href?: string | null }> = ({ label, href: rawHref }) => {
    const href = safeHref(rawHref);
    if (!href) {
        return (
            <div className="flex items-center justify-between py-2 opacity-40 cursor-not-allowed">
                <span className="text-sm text-gray-400">{label}</span>
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-300" />
            </div>
        );
    }

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between py-2 group"
        >
            <span className="text-sm text-gray-800 group-hover:text-gray-600 transition-colors">
                {label}
            </span>
            <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 text-gray-400 group-hover:text-gray-600 transition-colors" />
        </a>
    );
};

const DetailField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex flex-col gap-0.5 py-1.5">
        <span className="text-sm font-semibold text-[#6B7280]">{label}</span>
        <span className="text-gray-800 text-sm">{value}</span>
    </div>
);

const PatientInfoPanel: React.FC<PatientInfoPanelProps> = ({
    patient,
    practiceName,
    practiceId,
    doctorName,
    manufacturer,
    profilePictureUrl,
    profileThumbnailUrl,
    version,
    mondayLinks,
    workQueueUrl,
    headerAction
}) => {
    const formattedDob = patient.patient_date_of_birth
        ? formatDateOnly(patient.patient_date_of_birth)
        : null;

    const hasContactInfo =
        patient.patient_email || patient.patient_phone || formattedDob || patient.patient_gender;
    const hasTreatmentInfo =
        patient.patient_case_status ||
        patient.patient_case_type ||
        patient.patient_sub_case_type ||
        practiceName;

    return (
        <div className="w-full h-full flex flex-col overflow-y-auto scrollbar-hide">
            {/* Patient identity */}
            <div className={headerAction ? 'relative px-6 pt-6 pb-4' : 'px-6 pt-6 pb-4'}>
                {headerAction && <div className="absolute top-2.5 left-3.5">{headerAction}</div>}
                <div className="flex justify-center mb-4">
                    <div className="w-24 h-24">
                        <Avatar
                            user={{ name: patient.name, email: patient.patient_email ?? '' }}
                            imageURL={profilePictureUrl}
                            thumbnailURL={profileThumbnailUrl}
                        />
                    </div>
                </div>
                <div className="flex items-center justify-center gap-2">
                    <h3 className="text-lg font-bold text-gray-800 text-center">{patient.name}</h3>
                    <VersionBadge value={version} />
                </div>
            </div>

            {/* Divider */}
            <div className="mx-6 border-t border-gray-200" />

            {/* Foldable sections */}
            <div className="px-6 py-3 flex flex-col gap-1 flex-1">
                {hasTreatmentInfo && (
                    <FoldableSection title="Treatment overview">
                        <div className="flex flex-col">
                            {patient.patient_case_status && (
                                <DetailField label="Status" value={patient.patient_case_status} />
                            )}
                            {patient.patient_case_type && (
                                <DetailField label="Case type" value={patient.patient_case_type} />
                            )}
                            {patient.patient_sub_case_type && (
                                <DetailField
                                    label="Treatment approach"
                                    value={patient.patient_sub_case_type}
                                />
                            )}
                            {manufacturer && (
                                <DetailField label="Manufacturer" value={manufacturer} />
                            )}
                            <DetailField
                                label="OrthoPulse"
                                value={
                                    patient.patient_orthopulse_status?.toLowerCase() === 'yes'
                                        ? 'Yes'
                                        : 'No'
                                }
                            />
                            {practiceName && (
                                <DetailField
                                    label="Practice"
                                    value={
                                        <PracticeDoctorLabel
                                            practiceName={practiceName}
                                            practiceId={practiceId}
                                            format="practice-only"
                                        />
                                    }
                                />
                            )}
                            {doctorName && <DetailField label="Doctor" value={doctorName} />}
                        </div>
                    </FoldableSection>
                )}

                {hasContactInfo && (
                    <FoldableSection title="Details">
                        <div className="flex flex-col">
                            {formattedDob && (
                                <DetailField label="Date of birth" value={formattedDob} />
                            )}
                            {patient.patient_email && (
                                <DetailField
                                    label="Email"
                                    value={formatEmail(patient.patient_email)}
                                />
                            )}
                            {patient.patient_phone && (
                                <DetailField
                                    label="Phone"
                                    value={formatPhone(patient.patient_phone)}
                                />
                            )}
                            {patient.patient_gender && (
                                <DetailField label="Gender" value={patient.patient_gender} />
                            )}
                        </div>
                    </FoldableSection>
                )}

                <FoldableSection title="Links">
                    <div className="flex flex-col">
                        <LinkRow label="Work Queue" href={workQueueUrl} />
                        <LinkRow label="Monday (Patient)" href={mondayLinks?.patientBoardUrl} />
                        <LinkRow label="Monday (Ortho)" href={mondayLinks?.orthoBoardUrl} />
                        <LinkRow label="Treatment plan" href={patient.patient_manufacturer_link} />
                        <LinkRow label="Dental Monitoring" href={patient.patient_dm_link} />
                    </div>
                </FoldableSection>
            </div>
        </div>
    );
};

export { PatientInfoPanel };
